import {
	AviaTraveller,
	ChangeTravellerDataTravellerAvailability,
	CheckoutOrderFragment,
	GetAccountInfoQuery,
	InfoMessagePositionsEnum,
	OrderStatus,
	TimelineStep
} from '@websky/graphql';
import { Boarding, Gate, Landing, Marker, ToAirport } from '../../../../../ServicesAggregation/src/Icons';
import { format, MOBILE_MIN_WIDTH } from '../../../../../utils';
import * as React from 'react';
import cn from 'classnames';
import { useToggleable } from '../../../../../hooks';
import { useSelector } from 'react-redux';
import { getGdsServicesBySegments, getOrder } from '../../../../store/order/selectors';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';
import { useTheme } from '../../../../../theme';
import {
	addTime,
	checkInternationalSegment,
	getOtherServices,
	getRecommendedCardsServices,
	getSuggestedServices,
	getTime,
	getTimeline
} from '../utils';
import { ServiceGroupType } from '../../../../../ServicesAggregation/types';
import Services from '../Services/Services';
import FlightInfoCard from '../../../../../FlightInfoCard';
import ServicesCardsList from '../ServicesCardsList/ServicesCardsList';
import ServiceAggregation from '../../../../../ServicesAggregation/src/ServiceAggregation';
import { ButtonBase } from '@mui/material';
import { ArrowForward } from '../../../../../Icons';
import { PassengersDetailsModal } from '../../../../../PassengersDetails';
import { isAfter, parse } from 'date-fns';
import { WidgetCart } from '@websky/every-travel/src/types';
import { EveryTravelService } from '../../../../types';
import { cityIn } from 'lvovich';
import { getEveryTravelPrice } from '../../../../store/orderPrice/selectors';
import EveryTravelWrapper from '../EveryTravelWrapper/EveryTravelWrapper';
import { EngineTheme as EveryTravelEngine } from '@websky/every-travel/src/theme';
import { SearchFormTheme } from '@websky/every-travel/src/Widget/CostaBrava/CostaBrava';
import OrderExareStatus from '../../OrderExareStatus/OrderExareStatus';
import { OwnProps } from '../../routes/Order';
import InfoMessages from '../../../../../InfoMessages';
import { useConfig } from '../../../../../context';
import OrderStatusGuard from '../../OrderStatusGuard/OrderStatusGuard';
import ErrorModal from '../../ErrorModal';

const ServicesAggregationIconMap = {
	[TimelineStep.ToAirport]: ToAirport,
	[TimelineStep.Airport]: Gate,
	[TimelineStep.Boarding]: Boarding,
	[TimelineStep.Landing]: Landing,
	InCity: Marker
};

interface Props {
	segmentId: string;
	setSegmentId: (id: string) => void;
	onNextSegment: () => void;
	userInfo?: GetAccountInfoQuery['CurrentUser'];
	refetchUser?: () => Promise<GetAccountInfoQuery>;
	goToRefund: React.DispatchWithoutAction;
	goToExchange: React.DispatchWithoutAction;
	everyTravelStyles?: EveryTravelEngine;
	everyTravelSearchFormStyles?: SearchFormTheme;
	everyTravelCart: WidgetCart;
	setEveryTravelCart: (cart: WidgetCart) => void;
	goToChangePassenger: OwnProps['goToChangePassenger'];
	needToConfirmCart?: boolean;
}

const Inner: React.FC<Props> = ({
	segmentId,
	userInfo,
	refetchUser,
	setSegmentId,
	onNextSegment,
	goToRefund,
	goToExchange,
	everyTravelStyles,
	everyTravelSearchFormStyles,
	everyTravelCart,
	setEveryTravelCart,
	needToConfirmCart,
	goToChangePassenger
}) => {
	const {
		Engine: {
			nas: { suggestedServicesOrder, suggestServicesTypes }
		}
	} = useConfig();

	const isMobile = useMediaQuery({ maxWidth: MOBILE_MIN_WIDTH });

	const { isOpen: isPassengersModalOpen, open: openPassengersModal, close: closePassengersModal } = useToggleable(
		false
	);

	const order: CheckoutOrderFragment = useSelector(getOrder) as CheckoutOrderFragment;
	const servicesGroupsArray = useSelector(getGdsServicesBySegments);

	const { segment } = order.flight.segments.find(segment => segment.segment.id === segmentId);
	const internationalSegment = checkInternationalSegment(segment);

	const { t } = useTranslation('ServiceAggregation');
	const theme = useTheme('Checkout').ServicesAggregationPage;

	const everyTravelPrice = useSelector(getEveryTravelPrice);
	const everyTravelSegmentId = order.additionalServices.everyTravel?.initializeParameters?.segmentId;

	const getServicesAggregationWithAllServices = React.useMemo(() => {
		return servicesGroupsArray.map(group => {
			if (everyTravelSegmentId === group.segmentId && everyTravelCart) {
				const services = [...group.services];

				everyTravelCart.items.forEach(item => {
					services.push({
						type: 'everyTravel',
						services: [
							{
								excursionId: item.excursionId,
								productId: item.id,
								title: item.name,
								price: item.price,
								imageUrl: item.image,
								date: item.date && format(parse(item.date, 'yyyy-MM-dd', new Date()), 'dd MMM'),
								time: item.time?.substr(0, 5)
							} as EveryTravelService
						],
						selectedServices: [],
						canAddService:
							(!everyTravelCart.isOrdered ||
								order.status === OrderStatus.New ||
								order.status === OrderStatus.Booked) &&
							order.status !== OrderStatus.Cancelled,
						step: 'InCity' as TimelineStep
					});
				});

				return {
					...group,
					services
				};
			}

			return group;
		});
	}, [servicesGroupsArray, everyTravelCart, segmentId, everyTravelPrice, order.status]);

	const servicesByAggregationTypes = React.useMemo(() => {
		return getTimeline(
			getServicesAggregationWithAllServices.find(group => group.segmentId === segmentId),
			segmentId,
			internationalSegment
		);
	}, [getServicesAggregationWithAllServices, segmentId, internationalSegment]);

	const servicesGroups = React.useMemo(() => {
		const servicesGroups: ServiceGroupType[] = [];

		servicesByAggregationTypes.forEach(group => {
			let timePeriod = group.type === TimelineStep.Landing ? segment.arrival.time : '';

			if (group.type === 'InCity' && !group.services.length) {
				return;
			}

			if (!timePeriod && group.type !== 'InCity') {
				if (group.minutes.from === group.minutes.to) {
					timePeriod = t('at {{time}}', {
						time: getTime(addTime(segment.departure.time, group.minutes.from * -1, segment.departure.date))
					});
				} else {
					timePeriod = t('{{timeFrom}}–{{timeTo}}', {
						timeFrom: getTime(
							addTime(segment.departure.time, group.minutes.from * -1, segment.departure.date)
						),
						timeTo: getTime(addTime(segment.departure.time, group.minutes.to * -1, segment.departure.date))
					});
				}
			}

			const availableServices = group.services.filter(
				service =>
					service.canAddService ||
					!!service.selectedServices.length ||
					service.type === 'everyTravel' ||
					service.type === 'upgrade'
			);

			let desc = '';

			if (group.type === TimelineStep.ToAirport) {
				desc = t(internationalSegment ? `${group.type}_international_desc` : `${group.type}_desc`);
			} else {
				desc = t(`${group.type}_desc`);
			}

			servicesGroups.push({
				type: group.type,
				icon: ServicesAggregationIconMap[group.type],
				directionLabel: t(group.type, {
					departureCity: segment.departure.airport.city.name,
					arrivalCity:
						group.type === 'InCity'
							? cityIn(segment.arrival.airport.city.name)
							: segment.arrival.airport.city.name
				}),
				description: desc,
				timePeriod: timePeriod,
				services: <Services segmentId={group.segmentId} services={availableServices} />,
				empty: !availableServices.length
			});
		});

		return servicesGroups;
	}, [servicesByAggregationTypes]);

	const nextFlight = React.useMemo(() => {
		if (+segmentId < order.flight.segments.length - 1) {
			const { arrival, departure } = order.flight.segments[+segmentId + 1].segment;

			return {
				arrival: arrival.airport.city.name,
				departure: departure.airport.city.name
			};
		}

		return null;
	}, [segmentId]);

	const suggestedServices = React.useMemo(() => {
		const servicesOnSegment = servicesGroupsArray.find(group => group.segmentId === segmentId);

		return getSuggestedServices(servicesOnSegment, suggestedServicesOrder);
	}, [servicesGroupsArray, segmentId, suggestedServicesOrder]);

	const recommendedCardsServices = React.useMemo(
		() =>
			getRecommendedCardsServices(suggestedServices, {
				isMobile,
				suggestServicesTypes
			}),
		[suggestedServices, isMobile, suggestServicesTypes]
	);

	const otherServices = React.useMemo(() => {
		const servicesOnSegment = servicesGroupsArray.find(group => group.segmentId === segmentId);

		return getOtherServices(servicesOnSegment);
	}, [servicesGroupsArray, segmentId]);

	const everyTravel = order.additionalServices.everyTravel;

	const isEveryTravelAvailable = React.useMemo(() => {
		const arrDate = order.additionalServices.everyTravel?.initializeParameters?.arrDate,
			depDate = order.additionalServices.everyTravel?.initializeParameters?.depDate;

		if (arrDate) {
			if (!depDate) {
				return true;
			}

			return isAfter(new Date(depDate), new Date(arrDate));
		}

		return false;
	}, [
		order.additionalServices.everyTravel?.initializeParameters?.arrDate,
		order.additionalServices.everyTravel?.initializeParameters?.depDate
	]);

	return (
		<div className={theme.wrapper}>
			<ErrorModal />

			<InfoMessages
				className={cn(theme.container, theme.topInfoMessages)}
				position={[InfoMessagePositionsEnum.bookingTop, InfoMessagePositionsEnum.default]}
				messages={order.messages}
			/>

			<InfoMessages
				className={cn(theme.container, theme.topInfoMessages)}
				position={[InfoMessagePositionsEnum.bookingTop, InfoMessagePositionsEnum.default]}
				messages={order.warnings}
			/>

			<OrderExareStatus
				goToChangePassenger={goToChangePassenger}
				goToRefund={goToRefund}
				goToExchange={goToExchange}
			/>

			<FlightInfoCard
				segmentId={segmentId}
				setSegmentId={segmentId => setSegmentId(segmentId.toString())}
				onPassengerDetails={openPassengersModal}
				order={order}
				className={theme.container}
				onControlTicket={() => {}}
				onDownloadTickets={() => {}}
			/>

			<OrderStatusGuard statuses={[OrderStatus.New, OrderStatus.Booked, OrderStatus.Confirmed]}>
				<ServicesCardsList
					title={t('Add to your order')}
					services={recommendedCardsServices}
					segmentId={segmentId}
					messages={order.messages}
				/>

				<InfoMessages
					position={InfoMessagePositionsEnum.serviceTop}
					messages={order.messages}
					className={theme.container}
				/>

				<OrderStatusGuard statuses={[OrderStatus.New, OrderStatus.Booked, OrderStatus.Confirmed]}>
					<ServiceAggregation serviceGroups={servicesGroups} userInfo={userInfo} refetchUser={refetchUser} />
				</OrderStatusGuard>

				<ServicesCardsList
					title={t('Other services')}
					services={otherServices}
					segmentId={segmentId}
					hideSelected={false}
				/>

				<InfoMessages
					position={InfoMessagePositionsEnum.serviceBottom}
					messages={order.messages}
					className={theme.container}
				/>

				{everyTravel && isEveryTravelAvailable && (
					<div style={{ display: segmentId === everyTravelSegmentId ? 'block' : 'none' }}>
						<EveryTravelWrapper
							everyTravel={everyTravel}
							segment={segment}
							order={order}
							setEveryTravelCart={setEveryTravelCart}
							everyTravelStyles={everyTravelStyles}
							everyTravelSearchFormStyles={everyTravelSearchFormStyles}
							needToConfirmCart={needToConfirmCart}
						/>
					</div>
				)}

				{nextFlight && (
					<div className={theme.buttons}>
						<ButtonBase className={theme.next} onClick={onNextSegment}>
							{t('Next flight {{departure}} — {{arrival}}', {
								departure: nextFlight.departure,
								arrival: nextFlight.arrival
							})}

							{ArrowForward}
						</ButtonBase>
					</div>
				)}
			</OrderStatusGuard>

			<PassengersDetailsModal
				isOpen={isPassengersModalOpen}
				onClose={closePassengersModal}
				orderId={order.id}
				orderStatus={order.status}
				travellers={order.travellers as AviaTraveller[]}
				changeTravellersAvailability={
					order.exareInfo?.changeTravellerDataAvailability as ChangeTravellerDataTravellerAvailability[]
				}
				goToChangePassenger={goToChangePassenger}
			/>
		</div>
	);
};

export default Inner;
